import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5qtfjqhs78ip5370bqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-sm text-teal-500 mb-3 tracking-wider font-medium">CONTACT</p>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">{t('contact')}</h2>
            <p className="text-base text-slate-600 mb-8 leading-relaxed">{t('subtitle')}</p>

            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{t('officeLocation')}</h3>
                  <p className="text-sm text-slate-600 whitespace-pre-line">{t('address')}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{t('phoneConsultation')}</h3>
                  <a href="tel:0503635288" className="text-sm text-teal-600 hover:text-teal-700 font-medium">
                    050-3635-2288
                  </a>
                  <p className="text-xs text-slate-500 mt-1">{t('businessHours')}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{t('emailConsultation')}</h3>
                  <a href="mailto:Jzhou6205@gmail.com" className="text-sm text-teal-600 hover:text-teal-700 font-medium break-all">
                    Jzhou6205@gmail.com
                  </a>
                  <p className="text-xs text-slate-500 mt-1">{t('emailResponse')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6">
              <h3 className="font-bold text-slate-900 mb-4">{t('accessTitle')}</h3>
              <div className="space-y-2 text-sm text-slate-600">
                <p className="flex items-start gap-2">
                  <i className="ri-train-line text-teal-500 mt-1"></i>
                  <span>{t('access1')}</span>
                </p>
                <p className="flex items-start gap-2">
                  <i className="ri-train-line text-teal-500 mt-1"></i>
                  <span>{t('access2')}</span>
                </p>
                <p className="flex items-start gap-2">
                  <i className="ri-train-line text-teal-500 mt-1"></i>
                  <span>{t('access3')}</span>
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 mt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-wechat-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">WeChat</h3>
                  <p className="text-xs text-slate-500 mb-3">QRコードを読み取ってご連絡ください</p>
                  <div className="w-32 h-32 rounded-xl overflow-hidden border-2 border-teal-100">
                    <img
                      src="https://static.readdy.ai/image/fa61325f1bcb10e63a715068dd996489/3634758164264d67e338f6c008744b84.jpeg"
                      alt="WeChat QR Code"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">{t('formTitle')}</h3>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-lg flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-teal-500 text-xl"></i>
                <div>
                  <p className="text-sm font-medium text-teal-900">{t('successMessage')}</p>
                  <p className="text-xs text-teal-700 mt-1">{t('successDesc')}</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <i className="ri-error-warning-line text-red-500 text-xl"></i>
                <div>
                  <p className="text-sm font-medium text-red-900">{t('errorMessage')}</p>
                  <p className="text-xs text-red-700 mt-1">{t('errorDesc')}</p>
                </div>
              </div>
            )}

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('nameLabel')} <span className="text-red-500">{t('required')}</span>
                </label>
                <input
                  type="text" id="name" name="name" required
                  value={formData.name} onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder={t('namePlaceholder')}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('emailLabel')} <span className="text-red-500">{t('required')}</span>
                  </label>
                  <input
                    type="email" id="email" name="email" required
                    value={formData.email} onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder={t('emailPlaceholder')}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('phoneLabel')} <span className="text-red-500">{t('required')}</span>
                  </label>
                  <input
                    type="tel" id="phone" name="phone" required
                    value={formData.phone} onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder={t('phonePlaceholder')}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('serviceLabel')} <span className="text-red-500">{t('required')}</span>
                </label>
                <select
                  id="service" name="service" required
                  value={formData.service} onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                >
                  <option value="">{t('servicePlaceholder')}</option>
                  <option value="visa">{t('serviceOption1')}</option>
                  <option value="company">{t('serviceOption2')}</option>
                  <option value="construction">{t('serviceOption3')}</option>
                  <option value="naturalization">{t('serviceOption4')}</option>
                  <option value="transport">{t('serviceOption5')}</option>
                  <option value="other">{t('serviceOption6')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('messageLabel')} <span className="text-red-500">{t('required')}</span>
                </label>
                <textarea
                  id="message" name="message" required
                  maxLength={500} rows={6}
                  value={formData.message} onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                  placeholder={t('messagePlaceholder')}
                ></textarea>
                <p className="text-xs text-slate-500 mt-1">{t('messageNote')}</p>
              </div>

              <button
                type="submit" disabled={isSubmitting}
                className="w-full bg-teal-500 text-white py-4 rounded-lg font-medium hover:bg-teal-600 transition-colors whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <><i className="ri-loader-4-line animate-spin"></i>{t('submitting')}</>
                ) : (
                  <><i className="ri-send-plane-fill"></i>{t('submitButton')}</>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
